import React, { useState, useEffect } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { ASSETS } from "../i18n/translations";
import { Menu, X } from "lucide-react";

const NAV_ITEMS = [
  { key: "services", href: "#services" },
  { key: "industries", href: "#industries" },
  { key: "process", href: "#process" },
  { key: "models", href: "#models" },
  { key: "about", href: "#about" },
  { key: "contact", href: "#contact" },
];

export default function Navbar() {
  const { lang, setLang, t, languages } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors ${
        scrolled ? "bg-white/95 backdrop-blur-xl border-[#1d428a]/20 shadow-[0_1px_0_rgba(29,66,138,0.05)]" : "bg-white/80 backdrop-blur-md border-[#1d428a]/10"
      }`}
    >
      <div className="px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-20 md:h-24">
          <a href="#top" data-testid="brand-logo" className="flex items-center gap-4 group">
            <img
              src={ASSETS.logo}
              alt="Schweizer Planmanufaktur GmbH"
              className="h-16 md:h-20 w-auto"
            />
            <div className="hidden md:block leading-tight border-l border-[#1d428a]/30 pl-4">
              <div className="font-display-mid text-xl md:text-2xl text-[#1d428a] tracking-wide">SCHWEIZER</div>
              <div className="font-mono-tech text-[#df4661] mt-1 text-[10px]">PLANMANUFAKTUR GMBH</div>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.key}
                href={item.href}
                data-testid={`nav-link-${item.key}`}
                className="font-mono-tech text-[#5A6270] hover:text-[#1d428a] link-underline transition-colors"
              >
                {t.nav[item.key]}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div
              data-testid="language-switcher"
              className="hidden sm:flex items-center border border-[#1d428a]"
            >
              {languages.map((l, idx) => (
                <button
                  key={l.code}
                  data-testid={`lang-btn-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`font-mono-tech px-2.5 py-1.5 transition-colors ${
                    lang === l.code
                      ? "bg-[#1d428a] text-white"
                      : "text-[#1d428a] hover:bg-[#1d428a]/10"
                  } ${idx !== languages.length - 1 ? "border-r border-[#1d428a]" : ""}`}
                  type="button"
                >
                  {l.label}
                </button>
              ))}
            </div>

            <a
              href="#contact"
              data-testid="nav-cta"
              className="hidden md:inline-flex items-center gap-2 bg-[#1d428a] text-white font-mono-tech px-5 py-3 hover:bg-[#df4661] transition-colors"
            >
              <span className="w-1.5 h-1.5 bg-[#df4661] group-hover:bg-white" />
              {t.nav.cta}
            </a>

            <button
              onClick={() => setOpen((s) => !s)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 border border-[#1d428a] text-[#1d428a]"
              aria-label="Toggle menu"
              type="button"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {open && (
          <div data-testid="mobile-menu" className="lg:hidden pb-6 border-t border-[#1d428a]/15 pt-4">
            <nav className="flex flex-col gap-1">
              {NAV_ITEMS.map((item) => (
                <a
                  key={item.key}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${item.key}`}
                  className="font-mono-tech text-[#5A6270] hover:text-[#1d428a] py-2"
                >
                  {t.nav[item.key]}
                </a>
              ))}
            </nav>
            <div className="flex items-center gap-2 mt-4">
              {languages.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  data-testid={`mobile-lang-btn-${l.code}`}
                  className={`font-mono-tech px-3 py-2 border ${
                    lang === l.code
                      ? "bg-[#1d428a] text-white border-[#1d428a]"
                      : "border-[#1d428a] text-[#1d428a]"
                  }`}
                  type="button"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
